import {
  FormControl,
  HStack,
  Text,
  TextInputProps,
} from '@metservicedev/met-ui';
import React from 'react';
import { Controller, FieldPath, FieldValues, get, useFormContext } from 'react-hook-form';

import { createStyles } from '../tss';
import { InfoToolTip } from './InfoToolTip';
import { ValidationMessage } from './ValidationMessage';

type DateTimePart = {
  id: string;
  label: string;
  maxNumber?: number;
  separator?: string;
  width: number;
};
const DATE_TIME_PARTS: DateTimePart[] = [
  { id: 'day', label: 'DD', maxNumber: 31, separator: '/', width: 26 },
  { id: 'month', label: 'MM', maxNumber: 12, separator: '/', width: 32 },
  { id: 'year', label: 'YYYY', separator: ' ', width: 40 },
  { id: 'hour', label: 'HH', maxNumber: 23, separator: ':', width: 28 },
  { id: 'minute', label: 'mm', maxNumber: 59, width: 24 },
];

const useStyles = createStyles(({ classes, theme }) => ({
  inputContainer: {
    position: 'relative',
    border: '1px solid var(--colors-border)',
    backgroundColor: 'var(--colors-surface)',
    borderColor: 'var(--colors-border)',
    width: '100%',
    borderRadius: 'var(--radii-xl)',
    gap: 0,
  },
  inputContainerError: {
    borderColor: 'var(--colors-border-error)',
  },
  label: {
    position: 'absolute',
    top: -8,
    left: 12,
    padding: 0,
    lineHeight: '1',
    color: 'var(--colors-text-subtle)',
    backgroundColor: 'var(--colors-surface)',
  },
  input: {
    boxSizing: 'content-box',
    boxShadow: 'none',
    outline: 'none',
    padding: `${theme.spacing(1)}px 0`,
    paddingLeft: theme.spacing(1),
    textAlign: 'center',
    '&:first-of-type': {
      paddingLeft: theme.spacing(2),
    },
    '&:last-of-type': {
      paddingRight: theme.spacing(2),
      flex: 1,
      textAlign: 'left',
    },
    [`&.${classes.disabled}`]: {
      backgroundColor: 'var(--colors-surface-disabled)',
      color: 'var(--colors-text-disabled)',
    },
  },
  separator: {
    pointerEvents: 'none',
    userSelect: 'none',
    width: 0,
    [`&.${classes.disabled}`]: {
      color: 'var(--colors-text-disabled)',
    },
  },
  disabled: {},
}));

type Props<T extends FieldValues> = {
  'data-testid'?: string;
  disabled?: boolean;
  fieldPath: FieldPath<T>;
  helperText?: string;
  label: string;
  tooltipContent?: string;
  validateMessage?: string;
  validatePattern?: RegExp;
  textInputProps?: TextInputProps;
  formControlProps?: React.ComponentProps<typeof FormControl>;
};

export const FormDateTimePicker = <T extends FieldValues>({
  'data-testid': testId,
  disabled,
  fieldPath,
  label,
  tooltipContent,
  formControlProps,
}: Props<T>): React.ReactElement<Props<T>> => {
  const { classes, cx } = useStyles();
  const { formState, control } = useFormContext<T>();
  const error = get(formState.errors, fieldPath);
  const inputClass = cx(classes.input, { [classes.disabled]: disabled });
  return (
    <Controller
      control={control}
      name={fieldPath}
      render={({ field: { onChange, onBlur, value, ref } }) => {
        const valueParts = String(value || '').split(/[/ :]/);
        const changePart = (index: number, newValue: string) => {
          const newParts = [...valueParts];
          newParts[index] = newValue;
          let combinedValue = '';
          DATE_TIME_PARTS.forEach((part, i) => {
            combinedValue += (newParts[i] ?? '');
            if (part.separator) {
              combinedValue += part.separator;
            }
          });
          onChange(combinedValue);
        };
        const focusIndex = (el: HTMLElement, index: number) => {
          const parent = el.parentElement;
          const target = parent?.querySelector(`input:nth-of-type(${index + 1})`);
          if (target && target.tagName === 'INPUT') {
            const inputEl = target as HTMLInputElement;
            inputEl.focus();
            inputEl.select();
          }
        };

        return (
          <FormControl
            {...formControlProps}
            disabled={disabled}
            invalid={!!error || undefined}
          >
            <input data-testid={testId} type="hidden" value={value ?? ''} />
            <HStack>
              <HStack
                className={cx(classes.inputContainer, {
                  [classes.inputContainerError]: error,
                })}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    onBlur();
                  }
                }}
                ref={ref}
              >
                <Text className={classes.label} fontSize="sm">
                  {label}
                </Text>
                {DATE_TIME_PARTS.map((part, index) => (
                  <React.Fragment key={part.label}>
                    <input
                      className={inputClass}
                      data-testid={`${testId}-${part.id}`}
                      disabled={disabled}
                      maxLength={part.label.length}
                      onBlur={(e) => {
                        let val = e.currentTarget.value;
                        if (val.length > 0 && val.length < part.label.length) {
                          val = val.padStart(part.label.length, '0');
                          changePart(index, val);
                        }
                      }}
                      onChange={(e) => {
                        let val = e.currentTarget.value.replace(/\D/g, '');
                        const num = parseInt(val, 10);

                        if (part.maxNumber !== undefined && num > part.maxNumber) {
                          val = String(part.maxNumber);
                        }

                        if (
                          val.length === part.label.length &&
                          e.currentTarget.selectionStart === val.length
                        ) {
                          focusIndex(e.currentTarget, index + 1);
                        }

                        changePart(index, val);
                      }}
                      onKeyDown={(e) => {
                        const target = e.currentTarget;
                        if (part.separator && e.key === part.separator) {
                          e.preventDefault();
                          focusIndex(target, index + 1);
                        } else if (e.key === 'ArrowRight' && target.selectionStart === target.value.length) {
                          e.preventDefault();
                          focusIndex(target, index + 1);
                        } else if (e.key === 'ArrowLeft' && target.selectionStart === 0) {
                          e.preventDefault();
                          focusIndex(target, index - 1);
                        }
                      }}
                      placeholder={part.label}
                      style={{ width: part.width }}
                      value={valueParts[index] || ''}
                    />
                    {part.separator && (
                      <Text
                        key={part.separator}
                        className={cx(classes.separator, { [classes.disabled]: disabled })}
                      >
                        {part.separator}
                      </Text>
                    )}
                  </React.Fragment>
                ))}
              </HStack>
              {tooltipContent && (
                <InfoToolTip
                  tooltipContent={tooltipContent}
                  data-testid={`${testId}-tooltip`}
                />
              )}
            </HStack>
            <ValidationMessage message={error?.message && String(error.message)} />
          </FormControl>
        );
      }}
    />
  );
};
